import {ApiExecutorEffect, ApiInterceptorRecord, ApiInterceptors, ApiRequestDs, ApiResponseDs, ClearInterceptorsArg, IApiExecutor} from "./type-declarations";
import ApiResponseCodes from "../../models/api-response-codes";
import axios, {AxiosError, AxiosInstance, AxiosResponse} from "axios";
import curlirize from 'axios-curlirize';

/**
 * The Api executor of this application.
 */
class ApiExecutor implements IApiExecutor {

    /**
     * Creates a placeholder response data-structure.
     *
     * * this method is primarily used for creating the response object when an error happens in the api call.
     * @param statusCode        The status code of the response.
     */
    private createPlaceholderResponseDs(statusCode: number): ApiResponseDs {
        return {
            code: statusCode,
            resultFlag: false,
            data: null,
            message: undefined,
            aborted: statusCode === ApiResponseCodes.aborted,
        };
    }

    /**
     * Executes the logic for when an error is thrown while the api call is being made or after the response of the
     * api call is received.
     * @param error     The error that was thrown.
     * @param request   The request object.
     * @param retried   A number to indicate if the request is being retried. (Internal use only)
     */
    private async onErrorReceived(error: AxiosError & {}, request: ApiRequestDs, retried: number): Promise<ApiResponseDs> {
        let response: ApiResponseDs;
        if (error.message === 'Network Error') {
            // We retry again.
            if (retried > 0) {
                return this.createPlaceholderResponseDs(ApiResponseCodes.serverNotResponded);
            }
            return await this.execute(request, retried + 1);
        }

        if (error.response) {
            // Request made and server responded
            response = this.createPlaceholderResponseDs(error.response?.status ?? ApiResponseCodes.serverNotResponded);
            response.baseResponse = error.response;
        } else if (error.request) {
            if (axios.isCancel(error)) {
                // cancelled / aborted
                response = this.createPlaceholderResponseDs(ApiResponseCodes.aborted);
            } else {
                // The request was made but no response was received
                response = this.createPlaceholderResponseDs(ApiResponseCodes.serverNotResponded);
            }
        } else {
            if (error.config?.signal?.aborted) {
                // cancelled / aborted
                response = this.createPlaceholderResponseDs(ApiResponseCodes.aborted);
            } else {
                // Something happened in setting up the request that triggered an Error
                response = this.createPlaceholderResponseDs(ApiResponseCodes.requestFailed);
            }
        }
        return response;
    }

    /**
     * Creates a response data-structure from the given response object and the statusCode.
     *
     * @param response      The response object.
     * @param statusCode    The status code of the response.
     */
    private createResponseDs(response: ApiResponseDs, statusCode: number): ApiResponseDs {
        const code = response.code ?? statusCode;
        return {
            ...response,
            resultFlag: response.resultFlag ?? false,
            data: response?.data,
            code: code,
            aborted: code === ApiResponseCodes.aborted,
        };
    }

    /**
     * Executes the logic for when the response of the api call is received without any axios-specific or http
     * specific errors.
     *
     * @param request       The request object.
     * @param response      The response object.
     */
    private onResponseReceived(request: ApiRequestDs, response: AxiosResponse): ApiResponseDs {
        let result: ApiResponseDs;
        if (!request.externalResponseDS) {
            const internalResponse: ApiResponseDs = response.data;
            result = this.createResponseDs(
                internalResponse,
                internalResponse.code ?? response.status,
            );
        } else {
            result = this.createResponseDs(
                {
                    code: response.status,
                    data: response.data,
                    resultFlag: true,
                },
                response.status,
            );
        }
        result.baseResponse = response;
        return result;
    }

    // -------------------------------------------------------- Instance --------------------------------------------------------

    /**
     * The effects that will be run after the execution.
     */
    private readonly effects: Array<ApiExecutorEffect>;

    /**
     * The cache of injected request interceptor ids
     * @private
     */
    private activeRequestInterceptorIds: Record<ApiInterceptorRecord<any>['id'], number>;

    /**
     * The cache of injected response interceptor ids
     * @private
     */
    private activeResponseInterceptorIds: Record<ApiInterceptorRecord<any>['id'], number>;

    /**
     * The instance with which all the api calls are handled.
     */
    private readonly instance: AxiosInstance;


    /**
     * Creates a new instance of the ApiExecutor.
     */
    constructor() {
        this.instance = axios.create();
        this.effects = [];
        this.activeRequestInterceptorIds = {};
        this.activeResponseInterceptorIds = {};
        curlirize(this.instance, () => void 0);
    }

    /**
     * Injects api interceptors to the axios instance used in this interface.
     * @param interceptors  the interceptors to be injected.
     */
    public injectInterceptors(interceptors: Partial<ApiInterceptors>): void {
        for (const interceptor of interceptors.request ?? []) {
            const cachedId = this.activeRequestInterceptorIds[interceptor.id];
            if (cachedId) {
                this.instance.interceptors.request.eject(cachedId);
            }
            this.instance.interceptors.request.use(interceptor.interceptor.onFulfilled, interceptor.interceptor.onRejected);
        }
        for (const interceptor of interceptors.response ?? []) {
            const cachedId = this.activeResponseInterceptorIds[interceptor.id];
            if (cachedId) {
                this.instance.interceptors.response.eject(cachedId);
            }
            this.instance.interceptors.response.use(interceptor.interceptor.onFulfilled, interceptor.interceptor.onRejected);
        }
    }

    /**
     * Clears all the interceptors that are injected.
     * @param arg           The arguments to clear the interceptors.
     */
    public clearInterceptors(arg?: ClearInterceptorsArg): void {
        if (!arg) {
            arg = {request: true, response: true};
        }
        if (arg.request) {
            this.instance.interceptors.request.clear();
            this.activeRequestInterceptorIds = {};
        }
        if (arg.response) {
            this.instance.interceptors.response.clear();
            this.activeResponseInterceptorIds = {};
        }
    }

    /**
     * Injects the given effects to the executor.
     * @param effects    The effects to be injected.
     */
    public injectEffects(effects: Array<ApiExecutorEffect>): void {
        for (const effect of effects) {
            const cachedEffectIndex = this.effects.findIndex(e => e.id === effect.id);
            if (cachedEffectIndex !== -1) {
                this.effects.splice(cachedEffectIndex, 1, effect);
            } else {
                this.effects.push(effect);
            }
        }
    }

    /**
     * Clears all the effects that are injected.
     */
    public clearEffects(): void {
        this.effects.splice(0, this.effects.length);
    }

    /**
     * Interpolates the request object with the default values.
     */
    private interpolateRequest(request: ApiRequestDs,): ApiRequestDs {
        return {
            ...request,
            showSuccessToast: request.showSuccessToast ?? false,
            showErrorToast: request.showErrorToast ?? true,
            showSubmitTicket: request.showSubmitTicket ?? true,
            externalResponseDS: request.externalResponseDS ?? false,
        }
    }

    /**
     * Executes the api call with the given args and then parses the response.
     * @param request       The request that will be executed.
     * @param retried       A number to indicate if the request is being retried. (Internal use only)
     */
    public async execute(request: ApiRequestDs, retried = 0): Promise<ApiResponseDs> {
        let response: ApiResponseDs;
        request = this.interpolateRequest(request);
        try {
            response = this.onResponseReceived(request, await this.instance.request(request));
        } catch (e: any) {
            response = await this.onErrorReceived(e, request, retried);
        }
        await this.runExecutionEffects(request, response);
        return response;
    }

    /**
     * Runs the effects registered in this executor after the execution of the api call.
     *
     * @param request       The request that was executed.
     * @param response      The response that was received.
     */
    private async runExecutionEffects(request: ApiRequestDs, response: ApiResponseDs): Promise<void> {
        for (const effect of this.effects ?? []) {
            await effect.effect(request, response);
        }
    }
}


export default ApiExecutor;
